import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Pill, StatusDot } from '../ui';
import type { Product } from '../featureLayout/products';
import type { ProductCardState } from './useHomeSummary';
import { PRODUCT_COPY, type HomeStat } from './homeSummary';

interface ProductSummaryCardProps {
  product: Product;
  state: ProductCardState;
  onRetry: () => void;
}

const OVERLINE = 'text-[11px] font-bold uppercase tracking-[.1em] text-gray-500';

const StatCell: React.FC<{ stat: HomeStat; first: boolean }> = ({ stat, first }) => (
  <div className={`min-w-0 px-3 py-2.5 ${first ? 'bg-[#F5F3FF]' : ''}`}>
    <div className={`${OVERLINE} truncate ${first ? '!text-[#5A21C9]' : ''}`}>{stat.label}</div>
    <div className={`text-[26px] font-bold leading-tight ${first ? 'text-[#5A21C9]' : 'text-gray-900'}`}>
      {stat.count}
    </div>
    <div className="truncate text-xs text-gray-600">{stat.caption}</div>
  </div>
);

const Skeleton: React.FC = () => (
  <div className="flex flex-col gap-3" data-testid="home-card-loading" aria-busy="true">
    <div className="h-[84px] animate-pulse rounded-xl bg-gray-100" />
    {[0, 1, 2].map((i) => (
      <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100" />
    ))}
  </div>
);

/**
 * One product card on the home page: title and description, three counts,
 * the most recent rows and a link to the product's home.
 */
const ProductSummaryCard: React.FC<ProductSummaryCardProps> = ({ product, state, onRetry }) => {
  const Icon = product.icon;
  const { summary } = state;
  const headingId = `home-card-${product.id}`;

  return (
    <section
      aria-labelledby={headingId}
      className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white"
    >
      <div className="flex flex-1 flex-col gap-4 p-[18px]">
        <header className="flex items-start gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: product.accent }}
            aria-hidden="true"
          >
            <Icon size={17} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <h2 id={headingId} className="text-base font-bold text-gray-900">
              {product.name}
            </h2>
            <p className="text-[13px] leading-snug text-gray-600">{PRODUCT_COPY[product.id].description}</p>
          </div>
        </header>

        {state.status === 'loading' && <Skeleton />}

        {state.status === 'error' && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-[#fecaca] bg-[#fee2e2] p-3 text-sm text-[#991b1b]"
          >
            <AlertCircle size={15} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden="true" />
            <p className="flex-1">We couldn't load {product.name}. Check your connection and try again.</p>
            <button
              type="button"
              onClick={onRetry}
              className="rounded-lg border border-[#991b1b] px-2.5 py-0.5 text-xs font-bold hover:bg-white/60"
            >
              Retry
            </button>
          </div>
        )}

        {summary && (
          <>
            <div className="grid grid-cols-3 divide-x divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
              {summary.stats.map((stat, index) => (
                <StatCell key={stat.id} stat={stat} first={index === 0} />
              ))}
            </div>

            <div className="flex flex-col">
              <div className={`${OVERLINE} mb-1.5`}>Recent</div>
              {summary.recent.length === 0 ? (
                <p className="rounded-lg bg-gray-50 px-3 py-4 text-sm text-gray-600">
                  No {summary.unitPlural} yet.{' '}
                  <Link
                    to={`${product.href}/new`}
                    className="font-bold text-[#5A21C9] hover:text-[#6E2EF0] hover:underline"
                  >
                    {summary.startNewLabel}
                  </Link>
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 border-y border-gray-100">
                  {summary.recent.map((item) => (
                    <li key={item.jobId}>
                      <Link
                        to={item.href}
                        className="flex items-center gap-2.5 px-1 py-2.5 hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                      >
                        <StatusDot tone={item.dot} size={8} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-gray-900">{item.title}</span>
                          {item.sub && <span className="block truncate text-xs text-gray-600">{item.sub}</span>}
                        </span>
                        <Pill tone={item.pillTone}>{item.pillLabel}</Pill>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      <footer className="border-t border-gray-100 px-[18px] py-3 text-right">
        <Link
          to={product.href}
          className="text-[13px] font-bold text-[#5A21C9] hover:text-[#6E2EF0] hover:underline"
        >
          {PRODUCT_COPY[product.id].viewAll} →
        </Link>
      </footer>
    </section>
  );
};

export default ProductSummaryCard;
